import { Company } from "@domain/CRM/company";
import { Deal } from "@domain/CRM/deal";
import { Filter, StandardAxiosConfig, getBaseDataURL } from "../../../common";
import type { User } from "@domain/Auth";
import { API } from "../../..";
import { apiClient } from "../../../client";
import { DataAPI_Base, GetAPI_Base } from "../base";

class GetAPI_Deals extends GetAPI_Base<Deal> {
	constructor() {
		super("deals", Deal);
	}

	public async activeAndPrimary(dealIds: string[], fresh?: boolean) {
		const filter = {
			type: "OR",
			fields: dealIds.map((id) => {
				return {
					type: "AND",
					fields: {
						id: id,
						isActive: true,
						isConfigOnly: false,
					},
				} as Filter<Deal>;
			}),
		} as Filter<Deal>;

		return super.byFilter(filter, fresh);
	}

	/**
	 * @param [loadAll=false]  Otherwise retrieves only active and primary deals
	 */
	public async byCompany(company: Company, loadAll: boolean = false, fresh?: boolean) {
		if (!company.dealIds || company.dealIds.length === 0) return [];
		const deals = loadAll
			? await super.byIds(company.dealIds, fresh)
			: await this.activeAndPrimary(company.dealIds, fresh);
		return deals;
	}

	public async byUser(user: Pick<User, "companyIdsAll">) {
		const companies = await API.Data.Companies.get.byUser(user);
		const company = companies[0];
		return this.byCompany(company);
	}

	/**
	 * Fetches every Deal belonging to any of the given companies whose season is
	 * one of `seasons`. Used by Deal Creator to avoid pulling all of a company's
	 * historical deals when only a single season's worth is needed.
	 */
	public async byCompaniesAndSeasons(companies: Company[], seasons: string[]): Promise<Deal[]> {
		return this.byCompanySeasonRequests(companies.map((company) => ({ company, seasons })));
	}

	/**
	 * Fetches deals for a list of (company, seasons) requests. Each request becomes
	 * an AND clause `(deal.id ∈ company.dealIds) AND (deal.season ∈ requestedSeasons)`,
	 * and the per-request clauses are combined with OR. Lets the Deal Creator's
	 * client-side cache request *only* the (company, season) pairs it doesn't
	 * already have rather than re-fetching every season for every company.
	 */
	public async byCompanySeasonRequests(requests: { company: Company; seasons: string[] }[]): Promise<Deal[]> {
		const clauses = requests
			.filter((r) => r.seasons.length > 0 && (r.company.dealIds?.length ?? 0) > 0)
			.map((r) => {
				const idFilter = {
					type: "OR",
					fields: (r.company.dealIds ?? []).map((id) => ({ id }) as Partial<Deal>),
				} as Filter<Deal>;
				const seasonFilter = {
					type: "OR",
					fields: r.seasons.map((s) => ({ season: s }) as Partial<Deal>),
				} as Filter<Deal>;
				return { type: "AND", fields: [idFilter, seasonFilter] } as Filter<Deal>;
			});
		if (clauses.length === 0) return [];
		if (clauses.length === 1) return super.byFilter(clauses[0]);
		return super.byFilter({ type: "OR", fields: clauses } as Filter<Deal>);
	}

	public async seasonOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("deals")}/options/season`;
		const response = await apiClient.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error("Failed to get season options");
		}
		return response.data;
	}

	public async selectFieldOptions(): Promise<Record<string, string[]>> {
		const url = `${getBaseDataURL("deals")}/options/select-fields`;
		const response = await apiClient.get<Record<string, string[]>>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error("Failed to get deal select field options");
		}
		return response.data;
	}
}

export class DealsAPI extends DataAPI_Base<Deal> {
	get: GetAPI_Deals;

	constructor() {
		super("deals", Deal);
		this.get = new GetAPI_Deals();
	}
}
